import type { Optional } from '@festina-life/core'
import type { ConstructorCall } from '@festina-life/fplus-monaco'
import { IconButton } from '@festina-life/ui/actions'
import { useTranslate } from '@festina-life/ui/hooks'
import { paperStyles } from '@festina-life/ui/libs'
import { typographyStyles } from '@festina-life/ui/structure'
import cx from 'classnames'
import { Fragment, useMemo } from 'react'

import { useTemplateContext } from '../../../context/templateContext'
import { useLazyLayerMetaModel } from '../../../context/useLazyLayerMetaModel'
import { useTemplateIsLibrary } from '../../../hooks/useTemplateIsLibrary'
import type { FPlusExpressionValue } from '../../../types/templateModel'
import { useApplicationViewOperationsContext } from '../../../workspace/applicationViewOperationsContext'
import { getParameterTypeInformation } from '../../fplusExpression/expressionType'
import { FPlusExpressionEditor } from '../../fplusExpression/FPlusExpressionEditor'
import { FPlusExpressionViewer } from '../../fplusExpression/FPlusExpressionViewer'
import type { ConstructorTypeInformation } from '../../fplusExpression/types'
import { getSimulatedFunctionForParameter } from './helpers'

type Props = {
  currentValue: ConstructorCall
  onChange: (nextValue: ConstructorCall) => void
  typeInformation: ConstructorTypeInformation
  expressionPath: string
}

export const FPlusSingleConstructorEditor = ({
  currentValue,
  expressionPath,
  onChange,
  typeInformation,
}: Props) => {
  const { templateVersion } = useTemplateContext()
  const isLibrary = useTemplateIsLibrary()
  const metaModel = useLazyLayerMetaModel(templateVersion?.layer?.layerId)
  const { applicationEnumDefinitions } = useApplicationViewOperationsContext()
  const translate = useTranslate()

  const { knownParameters, unknownParameters } = useMemo(() => {
    const argumentMap = new Map<string, string>(
      currentValue.arguments.map(({ name, value }) => [name, value]),
    )

    const knownParameters = typeInformation.parameters
      .map((parameter) => ({
        ...parameter,
        value: argumentMap.get(parameter.name),
      }))
      .sort((parameterA, parameterB) =>
        parameterA.name.localeCompare(parameterB.name, undefined, {
          sensitivity: 'base',
        }),
      )

    const unknownParameters = argumentMap
      .entries()
      .filter(
        ([name]) =>
          !knownParameters.some((parameter) => parameter.name === name),
      )
      .toArray()

    return { knownParameters, unknownParameters }
  }, [currentValue.arguments, typeInformation.parameters])

  const handleParameterChanged =
    (parameter: (typeof knownParameters)[number]) =>
    (nextValue: Optional<FPlusExpressionValue>) => {
      onChange({
        ...currentValue,
        arguments: currentValue.arguments.some(
          (argurment) => argurment.name === parameter.name,
        )
          ? currentValue.arguments.map((argument) =>
              argument.name === parameter.name
                ? { ...argument, value: nextValue?.fplusSource ?? '' }
                : argument,
            )
          : [
              ...currentValue.arguments,
              { name: parameter.name, value: nextValue?.fplusSource ?? '' },
            ],
      })

      return Promise.resolve()
    }

  const handleRemoveParameter = (name: string) => () => {
    onChange({
      ...currentValue,
      arguments: currentValue.arguments.filter(
        (argument) => argument.name !== name,
      ),
    })
  }

  return (
    <div
      className={cx(
        'grid grid-cols-[auto_1fr] text-amaze-ink dark:text-amaze-inkBright h-full w-[calc(100%-20px)] items-center rounded-md border-slate-300 dark:border-slate-500 border-dashed',
        paperStyles({ borders: ['all'] }),
      )}
    >
      {!knownParameters.length && !unknownParameters.length && (
        <span
          className={cx('p-2', typographyStyles({ intensity: 'secondary' }))}
        >
          {translate('templateViewer.constructorEditor.emptyMessage', {
            typeName: currentValue.name,
          })}
        </span>
      )}
      {knownParameters.map((parameter) => {
        const parameterTypeInformation = getParameterTypeInformation({
          parameter,
          metaModel,
          configuredEnums: applicationEnumDefinitions,
          parent: typeInformation,
        })

        return (
          <Fragment key={parameter.name}>
            <div
              className={cx(
                'flex flex-col h-full p-2 justify-center border-slate-300 dark:border-slate-500 border-dashed',
                paperStyles({ borders: ['all'] }),
              )}
            >
              {parameter.name}
              <div
                className={cx(
                  'opacity-60',
                  typographyStyles({ size: 'sm', intensity: 'secondary' }),
                )}
              >
                {parameter.type?.typeName}
              </div>
            </div>
            <div
              className={cx(
                'size-full relative border-slate-300 dark:border-slate-500 border-dashed',
                paperStyles({ borders: ['all'] }),
              )}
            >
              <FPlusExpressionEditor
                path={expressionPath}
                expression={{ fplusSource: parameter.value }}
                fallbackCode={parameter.value ?? ''}
                onChange={handleParameterChanged(parameter)}
                type={
                  templateVersion &&
                  isLibrary(templateVersion?.versionInfo.identifier ?? '')
                    ? 'library'
                    : 'template'
                }
                typeInformation={parameterTypeInformation}
                shouldShowOverflowFallback={false}
                simulatedFunctionDeclaration={
                  (parameterTypeInformation ?? typeInformation)
                    ? getSimulatedFunctionForParameter(
                        parameterTypeInformation ?? typeInformation,
                        parameter.type?.typeName ?? '',
                      )
                    : undefined
                }
                isNested
                isTransparent
              />
              <IconButton
                iconName="MinusCircle"
                isDanger
                onClick={handleRemoveParameter(parameter.name)}
                className={cx(`absolute! -right-6 top-1/2 -translate-y-1/2`, {
                  invisible: !parameter.value,
                })}
              />
            </div>
          </Fragment>
        )
      })}
      {!!unknownParameters.length && (
        <div className="col-span-3 text-center">
          {translate('templateViewer.constructorEditor.invalidParameters')}
        </div>
      )}
      {unknownParameters.map(([name, value]) => (
        <Fragment key={name}>
          <div
            className={cx(
              'flex-row justify-between rounded-md p-1 opacity-50',
              paperStyles({ isFaded: true }),
            )}
          >
            {name}
          </div>
          <div
            className={cx(
              'rounded-md p-1 opacity-50',
              paperStyles({ isFaded: true }),
            )}
          >
            <FPlusExpressionViewer
              expression={{ fplusSource: value }}
              isTransparent
              path={expressionPath}
              shouldShowOverflowFallback={false}
              alignment="left"
              isShowingFunctionDeclaration={false}
              type={
                templateVersion &&
                isLibrary(templateVersion.versionInfo.identifier ?? '')
                  ? 'library'
                  : 'template'
              }
            />
          </div>
          <IconButton
            iconName="Trash"
            isDanger
            onClick={handleRemoveParameter(name)}
          />
        </Fragment>
      ))}
    </div>
  )
}
